import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { Flashcard } from '@/mocks/lessons';

interface FlashcardsProps {
  cards: Flashcard[];
}

export default function Flashcards({ cards }: FlashcardsProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState<Set<string>>(new Set());
  const [unknown, setUnknown] = useState<Set<string>>(new Set());

  const card = cards[currentIndex];
  const progress = ((currentIndex + 1) / cards.length) * 100;

  const handleNext = () => {
    if (currentIndex < cards.length - 1) {
      setFlipped(false);
      setTimeout(() => setCurrentIndex((i) => i + 1), 150);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setFlipped(false);
      setTimeout(() => setCurrentIndex((i) => i - 1), 150);
    }
  };

  const handleKnown = () => {
    setKnown((prev) => new Set(prev).add(card.id));
    setUnknown((prev) => {
      const next = new Set(prev);
      next.delete(card.id);
      return next;
    });
    handleNext();
  };

  const handleUnknown = () => {
    setUnknown((prev) => new Set(prev).add(card.id));
    setKnown((prev) => {
      const next = new Set(prev);
      next.delete(card.id);
      return next;
    });
    handleNext();
  };

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/30' : 'text-black/30',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/50' : 'text-black/40',
    cardFrontBg: isDark ? 'bg-[#111111]' : 'bg-white',
    cardFrontBorder: isDark ? 'border-white/[0.06]' : 'border-black/[0.06]',
    cardBackBg: isDark ? 'bg-[#161616]' : 'bg-neutral-50',
    cardBackBorder: isDark ? 'border-white/[0.08]' : 'border-black/[0.08]',
    cardHint: isDark ? 'text-white/15' : 'text-black/15',
    cardTextBack: isDark ? 'text-white/80' : 'text-black/80',
    cardIcon: isDark ? 'text-white/20' : 'text-black/20',
    progressDone: isDark ? 'bg-emerald-500/60' : 'bg-emerald-500',
    progressReview: isDark ? 'bg-amber-500/60' : 'bg-amber-500',
    progressDefault: isDark ? 'bg-white/30' : 'bg-black/30',
    progressEmpty: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]',
    btnArrow: isDark ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-black/[0.04] border-black/[0.06]',
    btnArrowIcon: isDark ? 'text-white/50' : 'text-black/40',
    btnReview: isDark ? 'bg-amber-500/10 border-amber-500/15 text-amber-400' : 'bg-amber-50 border-amber-200/50 text-amber-600',
    btnKnown: isDark ? 'bg-emerald-500/10 border-emerald-500/15 text-emerald-400' : 'bg-emerald-50 border-emerald-200/50 text-emerald-600',
    trophy: isDark ? 'text-white/40' : 'text-black/40',
    trophyText: isDark ? 'text-white/60' : 'text-black/60',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-stack-line ${t.iconMuted} text-sm`} />
          </div>
        </div>
        <div>
          <h3 className={`${t.textPrimary} text-sm font-semibold`}>Cue Cards</h3>
          <p className={`${t.textFaint} text-[11px]`}>{cards.length} cards &middot; {known.size} known &middot; {unknown.size} to review</p>
        </div>
      </div>

      {/* Progress */}
      <div className="flex gap-1 mb-5">
        {cards.map((_, i) => (
          <div
            key={i}
            className={`h-[2px] flex-1 rounded-full transition-colors duration-300 ${
              i < currentIndex
                ? known.has(cards[i].id)
                  ? t.progressDone
                  : unknown.has(cards[i].id)
                    ? t.progressReview
                    : t.progressDefault
                : i === currentIndex
                  ? t.progressDefault
                  : t.progressEmpty
            }`}
          />
        ))}
      </div>

      {/* Card */}
      <div className="perspective-[1200px] mb-5">
        <motion.div
          className="relative w-full aspect-[4/3] cursor-pointer"
          onClick={() => setFlipped(!flipped)}
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          style={{ transformStyle: 'preserve-3d' }}
        >
          {/* Front */}
          <div
            className={`absolute inset-0 rounded-2xl ${t.cardFrontBg} border ${t.cardFrontBorder} flex flex-col items-center justify-center px-6 text-center`}
            style={{ backfaceVisibility: 'hidden' }}
          >
            <div className="w-8 h-8 flex items-center justify-center mb-4">
              <i className={`ri-question-line ${t.cardIcon} text-lg`} />
            </div>
            <p className={`${t.textPrimary} text-sm font-semibold leading-relaxed`}>{card.front}</p>
            <p className={`${t.cardHint} text-[10px] mt-4`}>Tap to flip</p>
          </div>

          {/* Back */}
          <div
            className={`absolute inset-0 rounded-2xl ${t.cardBackBg} border ${t.cardBackBorder} flex flex-col items-center justify-center px-6 text-center`}
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <div className="w-8 h-8 flex items-center justify-center mb-4">
              <i className={`ri-lightbulb-line ${t.cardIcon} text-lg`} />
            </div>
            <p className={`${t.cardTextBack} text-sm leading-relaxed`}>{card.back}</p>
          </div>
        </motion.div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <motion.button
          onClick={handlePrev}
          whileTap={{ scale: 0.88 }}
          disabled={currentIndex === 0}
          className={`w-10 h-10 rounded-full ${t.btnArrow} border flex items-center justify-center cursor-pointer disabled:opacity-20 transition-opacity`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-arrow-left-line ${t.btnArrowIcon} text-xs`} />
          </div>
        </motion.button>

        <div className="flex gap-2.5">
          <motion.button
            onClick={handleUnknown}
            whileTap={{ scale: 0.9 }}
            className={`px-4 py-2 rounded-full border text-xs font-semibold flex items-center gap-1.5 cursor-pointer ${t.btnReview}`}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-restart-line text-[10px]" />
            </div>
            Review Again
          </motion.button>
          <motion.button
            onClick={handleKnown}
            whileTap={{ scale: 0.9 }}
            className={`px-4 py-2 rounded-full border text-xs font-semibold flex items-center gap-1.5 cursor-pointer ${t.btnKnown}`}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-check-line text-[10px]" />
            </div>
            Got It
          </motion.button>
        </div>

        <motion.button
          onClick={handleNext}
          whileTap={{ scale: 0.88 }}
          disabled={currentIndex === cards.length - 1}
          className={`w-10 h-10 rounded-full ${t.btnArrow} border flex items-center justify-center cursor-pointer disabled:opacity-20 transition-opacity`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-arrow-right-line ${t.btnArrowIcon} text-xs`} />
          </div>
        </motion.button>
      </div>

      <AnimatePresence>
        {currentIndex >= cards.length - 1 && (known.size + unknown.size >= cards.length) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-5 text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-trophy-line ${t.trophy} text-sm`} />
              </div>
              <span className={`${t.trophyText} text-xs font-semibold`}>Deck Complete</span>
            </div>
            <p className={`${t.textFaint} text-[11px]`}>
              {known.size} of {cards.length} cards mastered
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}